import './ApplianceInfo.scss';

import React, { useRef, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { useTranslation } from 'react-i18next';

import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import ApplianceAccordion from './ApplianceAccordion';
import { ApplianceDocuments } from './';
import DroletButton from '../Primitives/DroletButton';
import { IAppliance } from '../../interfaces';
import { IRetailer } from '../../interfaces/interfaces';
import Listing from '../Primitives/Listing';
import { useAllProducts } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useAllProducts';
import useMobileDetect from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useMobileDetect';

import GatsbyImageWrapper from '../GatsbyImageWrapper';
import { ProductPreviewVisao } from './ProductPreviewVisao';
import ModalProductPreviewVisao from './ModalProductPreviewVisao';

export default function ApplianceInfo(props: {
    appliance: IAppliance;
    visaoId?: string;
    accordionState: boolean[];
    updateAccordionState: (index: number) => void;
}) {
    const { t } = useTranslation('layout');
    const { currentLocale, navigateTranslated } = usePageLocalization();
    const { appliance, accordionState, updateAccordionState, visaoId } = props;
    const isMobile = useMobileDetect().isMobile();
    const isTablet = useMobileDetect().isTablet();
    const [allProducts] = useAllProducts();
    const isAndroid = useMobileDetect().isAndroid();

    const [showModal, setShowModal] = useState<boolean>(false);
    const { retailers, notes, clearanceFooter } = useStaticQuery(
        graphql`
            query {
                retailers: allProductsJson(
                    filter: { category: { regex: "^/Drolet/CMS/Retailers/" } }
                ) {
                    nodes {
                        code
                        title {
                            en_CA
                            en_US
                            fr_CA
                        }
                        fields {
                            ...productsFieldsImagesFragment
                        }
                        attributes {
                            url {
                                values {
                                    en_CA
                                    en_US
                                    fr_CA
                                }
                            }
                            available_country {
                                values {
                                    en_CA
                                    id
                                }
                            }
                        }
                    }
                }
                notes: allProductsJson(
                    filter: { category: { eq: "/Drolet/CMS/Notes/" } }
                ) {
                    nodes {
                        id
                        code
                        attributes {
                            specification_notes {
                                values {
                                    fr_CA
                                    en_US
                                    en_CA
                                    id
                                }
                                name {
                                    en_CA
                                    en_US
                                    fr_CA
                                }
                                index
                            }
                        }
                    }
                }
                clearanceFooter: allProductsJson(
                    filter: { code: { eq: "drolet_clearance-footer" } }
                ) {
                    nodes {
                        code
                        attributes {
                            content {
                                values {
                                    en_CA
                                    en_US
                                    fr_CA
                                }
                                valuesMarkdownRemark {
                                    en_CA
                                    en_US
                                    fr_CA
                                }
                            }
                        }
                    }
                }
                govCreditData: allProductsJson(
                    filter: { code: { eq: "drolet_tax-credit-usa" } }
                ) {
                    nodes {
                        category
                        code
                        title {
                            en_US
                        }
                        documents {
                            en_US
                        }
                    }
                }
            }
        `
    );

    const specificationMap = new Map();
    const applianceCategory = appliance.category;
    let filteredNotes = notes?.nodes;
    let noteType = '';
    if (applianceCategory.includes('Pellet Stoves')) {
        noteType = 'pellet';
        filteredNotes = notes?.nodes.filter((spec: { code: string }) =>
            spec?.code.includes(noteType)
        );
    } else if (applianceCategory.includes('Wood Furnaces')) {
        noteType = 'furnace';
        filteredNotes = notes?.nodes.filter((spec: { code: string }) =>
            spec?.code.includes(noteType)
        );
    } else {
        filteredNotes = notes?.nodes.filter(
            (spec: { code: string }) =>
                !spec?.code.includes('pellet') &&
                !spec?.code.includes('furnace')
        );
    }

    // Obtention des notes selon la catégory de l'appareil
    if (noteType === 'pellet' || noteType === 'furnace') {
        filteredNotes.forEach((spec: any, index: number) => {
            specificationMap.set(
                spec?.code.replace(`specification_notes_${noteType}_`, ''),
                {
                    options: spec?.attributes?.specification_notes,
                    index,
                }
            );
        });
    } else {
        filteredNotes.forEach((spec: any, index: number) => {
            specificationMap.set(
                spec?.code.replace(`specification_notes_`, ''),
                {
                    options: spec?.attributes?.specification_notes,
                    index,
                }
            );
        });
    }
    const textndIds = new Map();
    let noteIndex = 1;
    Array.from(specificationMap).forEach((spec: any) => {
        spec[1]?.options?.values.forEach((text: any) => {
            if (!textndIds.get(text?.id) && isPresentInValues(spec[0])) {
                textndIds.set(text?.id, { text, index: noteIndex });
                noteIndex++;
            }
        });
    });
    const notesReff = useRef(null);
    const scrollToRef = (ref: any) => {
        if (typeof window !== 'undefined') {
            window.scrollTo({
                left: 0,
                top: ref.current.offsetTop - window.innerHeight / 2,
                behavior: 'smooth',
            });
        }
    };

    const navigateToVisaoPage = (visaoId: string) => {
        const lang = currentLocale === 'fr_CA' ? 'fr' : 'en';
        const a: HTMLAnchorElement = document.createElement('a');
        a.target = '_blank';
        a.href = `https://demo.visao.ca/viewer/${visaoId}?lang=${lang}&ar=true`;
        a.click();
    };

    const startAR = () => {
        if (isAndroid) {
            navigateToVisaoPage(visaoId!);
        } else {
            setShowModal(true);
        }
    };

    let category: string;
    switch (appliance.category.split('/')[3]) {
        case 'Wood Stoves':
            category = 'stoves-inserts';
            break;
        case 'Pellet Stoves':
            category = 'pellet-stoves';
            break;
        case 'Camp Stove':
            category = 'camp-stove';
            break;
        case 'Wood Inserts':
            category = 'stoves-inserts';
            break;
        case 'Wood Furnaces':
            category = 'wood-furnaces';
            break;
        case 'Cookstoves':
            category = 'cook-stoves';
            break;
        case 'Outdoor Fireplaces':
            category = 'others';
            break;
        case 'Gas Wall Mounted Room Heater':
            category = 'others';
            break;
        default:
            category = 'stoves-inserts';
            break;
    }
    const onlineRetailerCodes =
        appliance?.online_retailers?.map(
            (retailer: { code: string }) => retailer.code
        ) || [];
    const storeRetailerCodes =
        appliance?.store_retailers?.map(
            (retailer: { code: string }) => retailer.code
        ) || [];
    const onlineRetailers = retailers?.nodes?.filter((node: any) =>
        onlineRetailerCodes.some(
            (retailerCode: string) =>
                node.code === retailerCode.replaceAll(' ', '-')
        )
    );
    const storeRetailers = retailers?.nodes?.filter((node: any) =>
        storeRetailerCodes.some(
            (retailerCode: string) =>
                node.code === retailerCode.replaceAll(' ', '-')
        )
    );
    const current_country =
        currentLocale.split('_')[1] === 'CA' ? 'Canada' : 'United States';
    const filteredStoreRetailers = storeRetailers.filter((retailer: any) => {
        const availableCountry =
            retailer?.attributes?.available_country?.values?.map(
                (e: any) => e?.en_CA
            );

        return !availableCountry || availableCountry.includes(current_country);
    });
    const filteredOnlineRetailers = onlineRetailers.filter((retailer: any) => {
        const availableCountry =
            retailer?.attributes?.available_country?.values?.map(
                (e: any) => e?.en_CA
            );

        return !availableCountry || availableCountry.includes(current_country);
    });
    return (
        <div className='appliance-info-container'>
            <Description />
            <Options productsJson={allProducts} />
            <Specifications />
            <ManuelDocuments />
            {(filteredOnlineRetailers?.length > 0 ||
                filteredStoreRetailers?.length > 0) && (
                <Retailers
                    onlineRetailers={filteredOnlineRetailers}
                    storeRetailers={filteredStoreRetailers}
                />
            )}
            <PartsWarranty />
        </div>
    );

    function PartsWarranty() {
        return (
            <div className='parts-warranty'>
                <DroletButton
                    label={t('appliance.part-title')}
                    onClick={() =>
                        navigateTranslated(
                            `archives/${appliance?.code?.toLowerCase()}/`
                        )
                    }
                />
                <DroletButton
                    label={t('appliance.warranty-title')}
                    onClick={() => navigateTranslated(`warranty/${category}/`)}
                />
            </div>
        );
    }

    function Description() {
        const showInMySpaceButton =
            !appliance?.slug?.['en_CA']?.includes('inserts');
        return (
            <ApplianceAccordion
                title={t('appliance.description')}
                open={accordionState[0]}
                onClick={() => updateAccordionState(0)}
            >
                <div className='appliance-descrption-container'>
                    <div
                        className='appliance-description'
                        dangerouslySetInnerHTML={{
                            __html: appliance?.description
                                ?.valuesMarkdownRemark?.[currentLocale],
                        }}
                    />
                    <div>
                        {visaoId && (
                            <ProductPreviewVisao
                                visaoId={visaoId as string}
                                product={appliance}
                            />
                        )}
                        {showInMySpaceButton && visaoId && (
                            <DroletButton
                                label={t('visao.ar-label') as string}
                                onClick={() => startAR()}
                            />
                        )}
                    </div>
                    <ModalProductPreviewVisao
                        isOpen={showModal}
                        visaoId={visaoId}
                        isStartAR={showModal}
                        handleModalClose={() => setShowModal(false)}
                        product={appliance}
                    />
                </div>
            </ApplianceAccordion>
        );
    }

    function Options(props: { productsJson: any }) {
        const { productsJson } = props;
        const products = productsJson.nodes;

        const options = appliance?.options
            ?.map((option: any) => {
                return products.find((prod: any) => prod.code === option.code);
            })
            .filter((prod: any) => prod !== undefined);

        const accessories = appliance?.accessories
            ?.map((accessory: any) => {
                return products.find(
                    (prod: any) => prod.code === accessory.code
                );
            })
            .filter((prod: any) => prod !== undefined);

        return (
            <ApplianceAccordion
                title={t('appliance.option-title')}
                open={accordionState[1]}
                onClick={() => updateAccordionState(1)}
            >
                {options && options.length > 0 && (
                    <Listing
                        title={t('appliance.recommended-for-installation')}
                        items={options}
                        displayManual={true}
                        displayLink={false}
                        hasOwnPage={true}
                    />
                )}

                {accessories && accessories.length > 0 && (
                    <Listing
                        title={t('appliance.perfect-complement')}
                        items={accessories}
                        displayManual={true}
                        displayLink={false}
                        hasOwnPage={true}
                    />
                )}
            </ApplianceAccordion>
        );
    }

    function Specifications() {
        return (
            <ApplianceAccordion
                title={t('appliance.specs-title')}
                open={accordionState[2]}
                onClick={() => updateAccordionState(2)}
            >
                <div className='appliance-spec-content'>
                    {getApplianceSpecTable()}
                </div>
            </ApplianceAccordion>
        );
    }

    function getApplianceSpecTable() {
        const allowDisplay = (value: string) => {
            switch (value) {
                case 'vuesIPEP': {
                    return false;
                }
                case 'meta_description': {
                    return false;
                }
                case 'meta_title': {
                    return false;
                }
                case 'suggested_appliances': {
                    return false;
                }
                case 'video_ids': {
                    return false;
                }
                case 'available_country': {
                    return false;
                }
                case 'visao_id': {
                    return false;
                }
                default: {
                    return true;
                }
            }
        };

        const allAttributes = appliance?.attributes;
        const attributesNotNull = Object.entries(allAttributes).filter(
            (attribute: [string, any]) => attribute[1] !== null
        );
        const currentAttributes = attributesNotNull.sort(
            (a: [string, any], b: [string, any]) =>
                (a[1]?.group as string)?.localeCompare(b[1]?.group, 'en') ||
                a[1]?.index - b[1]?.index
        );
        const filteredAttributes = currentAttributes.filter(
            (attribute: [string, any]) =>
                attribute[1] !== null &&
                allowDisplay(attribute[0]) &&
                attribute[1]?.group !== 'Degagements'
        );
        const attributesLeft = filteredAttributes.slice(
            0,
            (filteredAttributes.length + 1) / 2
        );
        const attributesRight = filteredAttributes.slice(
            (filteredAttributes.length + 1) / 2,
            filteredAttributes.length
        );

        const clearanceArray = Object.entries(appliance.attributes).filter(
            (attribute: [string, any]) => {
                return attribute[1] && attribute[1].group === 'Degagements';
            }
        );

        // S'il n'y as pas un nombre pair d'attribut, on créer un attribut vide dans le
        // tableau de droite pour le tout soit bien aligné
        if (attributesLeft.length > attributesRight.length) {
            attributesRight.push([
                'empty',
                {
                    group: 'none',
                    index: 99999,
                    name: { en_CA: '', en_US: '', fr_CA: '' },
                    values: [{ en_CA: '', en_US: '', fr_CA: '' }],
                },
            ]);
        }

        return (
            <>
                {!isTablet && isMobile ? (
                    <table>
                        <tbody>
                            {filteredAttributes.map(
                                (attribute: [string, any]) => (
                                    <AttributesElem
                                        key={attribute[0]}
                                        attribute={attribute[0]}
                                    />
                                )
                            )}

                            {ClearanceAttributes(clearanceArray)}
                        </tbody>
                    </table>
                ) : (
                    <div className='specs-table'>
                        <table>
                            <tbody>
                                {attributesLeft.map(
                                    (attribute: [string, any]) => (
                                        <AttributesElem
                                            key={attribute[0]}
                                            attribute={attribute[0]}
                                        />
                                    )
                                )}
                            </tbody>
                        </table>
                        <table>
                            <tbody>
                                {attributesRight.map(
                                    (attribute: [string, any]) => (
                                        <AttributesElem
                                            key={attribute[0]}
                                            attribute={attribute[0]}
                                        />
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {!isMobile && (
                    <table className='clearance-table'>
                        <tbody>{ClearanceAttributes(clearanceArray)}</tbody>
                    </table>
                )}

                <span
                    className='footnote'
                    dangerouslySetInnerHTML={{
                        __html: clearanceFooter.nodes[0]?.attributes?.content
                            ?.valuesMarkdownRemark?.[0]?.[currentLocale],
                    }}
                />
                <ul className='spec-footnotes'>{getNoteDesc()}</ul>
            </>
        );
    }
    function ClearanceAttributes(clearanceArray: any) {
        const displayPipes =
            appliance?.category.includes('Wood Stoves') ||
            appliance?.category.includes('Camp Stove') ||
            appliance?.category.includes('Cookstoves');

        function stoveTableRow(attribute: any, index: number) {
            return (
                <tr className='clearance-spec-row' key={index}>
                    <th className='spec-title'>
                        {`${t('appliance.clearance')} - ${
                            clearanceArray[index][1].name[currentLocale]
                        }${attribute[0].includes('Des') ? '**' : ''}`}
                    </th>
                    <td className='spec-value'>
                        <span>
                            {
                                clearanceArray[
                                    clearanceArray[index]?.[0] == 'FrontCi'
                                        ? index
                                        : index + 2
                                ]?.[1]?.values[0][currentLocale]
                            }
                        </span>
                        <span>
                            {
                                clearanceArray[
                                    clearanceArray[index]?.[0] == 'FrontCi'
                                        ? index + 1
                                        : index
                                ]?.[1]?.values[0][currentLocale]
                            }
                        </span>
                        <span>
                            {
                                clearanceArray[
                                    clearanceArray[index]?.[0] == 'FrontCi'
                                        ? index + 3
                                        : index + 3
                                ]?.[1]?.values[0][currentLocale]
                            }
                        </span>
                        <span>
                            {
                                clearanceArray[
                                    clearanceArray[index]?.[0] == 'FrontCi'
                                        ? index + 2
                                        : index + 1
                                ]?.[1]?.values[0][currentLocale]
                            }
                        </span>
                    </td>
                </tr>
            );
        }

        function insertTableRow(attribute: any, index: number) {
            return (
                <tr className='clearance-spec-row' key={index}>
                    <th className='spec-title'>
                        {`${t('appliance.clearance')} - ${
                            clearanceArray[index][1].name[currentLocale]
                        }${attribute[0].includes('Des') ? '**' : ''}`}
                    </th>
                    <td className='spec-value' colSpan={2}>
                        <span>
                            {
                                clearanceArray[index]?.[1]?.values[0][
                                    currentLocale
                                ]
                            }
                        </span>
                        <span>
                            {clearanceArray[index + 1]?.[1]?.values[0].fr_CA}
                        </span>
                    </td>
                </tr>
            );
        }

        const displayCountry =
            appliance?.category.includes('Wood Inserts') ||
            appliance?.category.includes('Wood Furnaces') ||
            appliance?.category.includes('Pellet Stoves') ||
            appliance?.category.includes('Gas Wall Mounted Room Heater');

        return (
            <>
                {(displayPipes || displayCountry) && (
                    <tr className='spec-row '>
                        <td className='spec-title clearance-title'>
                            <span>{t('appliance.clearance-title')}*</span>
                        </td>
                        <td className='spec-value countries'>
                            <div>
                                <span>{t('appliance.canada')}</span>
                            </div>
                            <div>
                                <span>{t('appliance.usa')}</span>
                            </div>
                        </td>
                    </tr>
                )}
                {displayPipes && (
                    <tr className='spec-row'>
                        <td className='spec-title pipes'></td>
                        <td className='spec-value pipes'>
                            <span>{t('appliance.single-pipe')}</span>
                            <span>{t('appliance.double-pipe')}</span>
                            <span>{t('appliance.single-pipe')}</span>
                            <span>{t('appliance.double-pipe')}</span>
                        </td>
                    </tr>
                )}

                {displayPipes
                    ? clearanceArray.map((attribute: any, index: number) => {
                          return (
                              index % 4 === 0 && stoveTableRow(attribute, index)
                          );
                      })
                    : clearanceArray.map((attribute: any, index: number) => {
                          return (
                              index % 2 === 0 &&
                              insertTableRow(attribute, index)
                          );
                      })}
            </>
        );
    }
    function AttributesElem(props: { attribute: string }) {
        if (
            props.attribute === 'empty' ||
            props.attribute === 'vide' ||
            props.attribute === '--'
        ) {
            return (
                <tr className='spec-row'>
                    <td className='spec-title'></td>
                    <td className='spec-value'></td>
                </tr>
            );
        }
        return (
            appliance?.attributes?.[props.attribute] && (
                <tr className='spec-row'>
                    <td className='spec-title'>
                        {
                            appliance?.attributes?.[props.attribute]?.name?.[
                                currentLocale
                            ]
                        }
                        {getApplianceIndex(props.attribute)}
                    </td>
                    <td className='spec-value'>
                        {
                            appliance?.attributes?.[props.attribute]
                                ?.values?.[0]?.[currentLocale]
                        }
                    </td>
                </tr>
            )
        );
    }
    function getNoteDesc() {
        return Array.from(textndIds).map((elem, index) => {
            return (
                <li
                    key={index}
                    ref={notesReff}
                >{`${elem[1]?.index}. ${elem[1]?.text[currentLocale]}`}</li>
            );
        });
    }
    function isPresentInValues(spec: any) {
        const presentSpecs = Object.entries(appliance.attributes).filter(
            (attribute: any) => {
                return (
                    attribute[1] &&
                    (attribute[1]?.group === 'Appliance performance' ||
                        attribute[1]?.group === 'General features')
                );
            }
        );

        return presentSpecs.find((presentSpec) => presentSpec[0] === spec);
    }

    function getApplianceIndex(arg0: string): React.ReactNode {
        const specification = specificationMap.get(arg0);

        if (specification) {
            // eslint-disable-next-line prefer-const
            let linkedIndex: any[] = [];
            const valuesArr = specification?.options?.values || [];
            valuesArr.forEach((value: any) => {
                linkedIndex.push(textndIds.get(value.id)?.index);
            });
            return (
                <span className='note-indicator'>
                    {linkedIndex.map((numb: any) => {
                        return (
                            <span className='notetxt' key={numb}>
                                {' '}
                                (
                                <span onClick={() => scrollToRef(notesReff)}>
                                    {numb}
                                </span>
                                )
                            </span>
                        );
                    })}
                </span>
            );
        }
        return '';
    }

    function ManuelDocuments() {
        return (
            <ApplianceAccordion
                title={t('appliance.manual-title')}
                open={accordionState[3]}
                onClick={() => updateAccordionState(3)}
            >
                <ApplianceDocuments
                    documents={appliance?.documents}
                    code={appliance.code}
                    vuesIPEP={appliance?.fields?.vuesIPEPImages}
                />
            </ApplianceAccordion>
        );
    }

    function Retailers(props: { onlineRetailers: any; storeRetailers: any }) {
        const { onlineRetailers, storeRetailers } = props;
        return (
            <ApplianceAccordion
                title={t('appliance.retailers-title')}
                open={accordionState[4]}
                onClick={() => updateAccordionState(4)}
            >
                {storeRetailers?.length > 0 && (
                    <RetailerSection
                        retailerList={storeRetailers}
                        title={t('appliance.retailers-in-store')}
                    ></RetailerSection>
                )}
                {onlineRetailers?.length > 0 && (
                    <RetailerSection
                        retailerList={onlineRetailers}
                        title={t('appliance.retailer-online')}
                    ></RetailerSection>
                )}
            </ApplianceAccordion>
        );
    }
    function RetailerSection(props: { retailerList: any[]; title: string }) {
        return (
            <>
                <div className='retailer-section-title'>{props.title}</div>
                <div className='appliance-retailers'>
                    {props.retailerList.map((retailer: IRetailer) => {
                        return (
                            <div
                                className='single-retailer'
                                key={retailer.code}
                            >
                                {retailer?.fields?.images?.[0] ? (
                                    <a
                                        href={
                                            retailer?.attributes?.url
                                                ?.values[0][currentLocale]
                                        }
                                        target='_blank'
                                        rel='noreferrer'
                                    >
                                        <GatsbyImageWrapper
                                            fields={retailer.fields}
                                        />
                                    </a>
                                ) : (
                                    <a
                                        href={
                                            retailer?.attributes?.url
                                                ?.values[0][currentLocale]
                                        }
                                        target='_blank'
                                        rel='noreferrer'
                                        className='no-image-retailer'
                                    >
                                        {retailer?.title[currentLocale]}
                                    </a>
                                )}
                            </div>
                        );
                    })}
                </div>
            </>
        );
    }
}
